/**
 * Favorites Service for managing favorite phrases ("Things I Say A Lot")
 * Implements CRUD operations for the favorite_phrases table
 */

import { supabaseService } from './supabaseClient';
import { FavoritePhrase } from '../types';

export interface CreateFavoriteRequest {
  label: string;
  text: string;
  tags?: string[];
  targetLang?: string;
}

export interface UpdateFavoriteRequest {
  label?: string;
  text?: string;
  tags?: string[];
  targetLang?: string;
  sortOrder?: number;
}

class FavoritesService {
  /**
   * Get all favorite phrases for the current user
   */
  async getFavorites(userId: string): Promise<FavoritePhrase[]> {
    const supabase = supabaseService.getClient();

    const { data, error } = await supabase
      .from('favorite_phrases')
      .select('*')
      .eq('user_id', userId)
      .order('sort_order', { ascending: true })
      .order('usage_count', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch favorite phrases: ${error.message}`);
    }

    return (data || []).map(this.mapDatabaseToFavorite);
  }

  /**
   * Add a new favorite phrase
   */
  async addFavorite(phrase: CreateFavoriteRequest): Promise<FavoritePhrase> {
    const { user } = await supabaseService.getCurrentUser();

    if (!user) {
      throw new Error('User must be authenticated to add favorites');
    }

    const supabase = supabaseService.getClient();

    const { data, error } = await supabase
      .from('favorite_phrases')
      .insert({
        user_id: user.id,
        label: phrase.label,
        text: phrase.text,
        tags: phrase.tags || [],
        target_lang: phrase.targetLang || null,
        usage_count: 0,
        sort_order: 0,
      })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A favorite phrase with this label already exists');
      }
      throw new Error(`Failed to add favorite phrase: ${error.message}`);
    }

    return this.mapDatabaseToFavorite(data);
  }

  /**
   * Update an existing favorite phrase
   */
  async updateFavorite(id: string, updates: UpdateFavoriteRequest): Promise<FavoritePhrase> {
    const { user } = await supabaseService.getCurrentUser();

    if (!user) {
      throw new Error('User must be authenticated');
    }

    const supabase = supabaseService.getClient();

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };

    if (updates.label !== undefined) updateData.label = updates.label;
    if (updates.text !== undefined) updateData.text = updates.text;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.targetLang !== undefined) updateData.target_lang = updates.targetLang;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;

    const { data, error } = await supabase
      .from('favorite_phrases')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A favorite phrase with this label already exists');
      }
      throw new Error(`Failed to update favorite phrase: ${error.message}`);
    }

    return this.mapDatabaseToFavorite(data);
  }

  /**
   * Delete a favorite phrase
   */
  async deleteFavorite(id: string): Promise<void> {
    const { user } = await supabaseService.getCurrentUser();

    if (!user) {
      throw new Error('User must be authenticated');
    }

    const supabase = supabaseService.getClient();

    const { error } = await supabase
      .from('favorite_phrases')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      throw new Error(`Failed to delete favorite phrase: ${error.message}`);
    }
  }

  /**
   * Increment usage count and update last_used timestamp
   */
  async incrementUsage(id: string): Promise<void> {
    const { user } = await supabaseService.getCurrentUser();

    if (!user) {
      return;
    }

    const supabase = supabaseService.getClient();

    // Get current usage_count, then increment
    const { data: current, error: fetchError } = await supabase
      .from('favorite_phrases')
      .select('usage_count')
      .eq('id', id)
      .eq('user_id', user.id)
      .single();

    if (fetchError) {
      console.error('Failed to fetch favorite phrase for usage update:', fetchError);
      return;
    }

    const { error } = await supabase
      .from('favorite_phrases')
      .update({
        usage_count: (current.usage_count || 0) + 1,
        last_used: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Failed to increment usage:', error);
      // Don't throw - this is non-critical
    }
  }

  /**
   * Map database row to FavoritePhrase type
   */
  private mapDatabaseToFavorite(data: any): FavoritePhrase {
    return {
      id: data.id,
      userId: data.user_id,
      label: data.label,
      text: data.text,
      tags: data.tags || [],
      targetLang: data.target_lang || undefined,
      usageCount: data.usage_count || 0,
      lastUsed: data.last_used ? new Date(data.last_used) : undefined,
      sortOrder: data.sort_order || 0,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at),
    };
  }
}

export const favoritesService = new FavoritesService();
